import { getApiUrl } from '@/lib/api-config';

export interface DashboardData {
  totalOutstanding: number;
  paidThisMonth: number;
  overdueBills: number;
  draftBills: number;
  openBills: number;
  totalBills: number;
  outstandingByAmount: Array<{ project: string; amount: number; percentage: number }>;
  committedButUnpaidByProject: Array<{ project: string; amount: number; count: number }>;
  openBillsByProject: Array<{ project: string; count: number; percentage: number }>;
  upcomingPayments: Array<{ bill: string; project: string; amount: number; daysUntilDue: number }>;
  cashSpent: Array<{ month: string; amount: number }>;
  apAging: Array<{ range: string; amount: number; count: number }>;
  billStatus: Array<{ status: string; count: number; percentage: number }>;
  topVendorsByAmountOwed: Array<{ vendor: string; amount: number; count: number }>;
}

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * The Bills dashboard. Every figure is computed by the backend (GET /api/bills/dashboard) from the same rows and rules
 * as the Bills / Payments tables, for the project selected globally ('all' = every project), in ONE request. `today` is
 * the user's own calendar day: "overdue" and "this month" are theirs, not the server's.
 */
export async function fetchBillsDashboard(projectId: string | undefined, signal?: AbortSignal): Promise<DashboardData> {
  const params = new URLSearchParams({ today: localToday() });
  if (projectId && projectId !== 'all') params.set('project_id', projectId);
  const response = await fetch(getApiUrl(`api/bills/dashboard?${params}`), { signal });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || 'The dashboard could not be loaded');
  return body.data as DashboardData;
}
